import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import UpgradeModal, { UpgradePromptConfig } from '../components/billing/UpgradeModal';
import { usePlan } from './PlanContext';

interface UpgradePromptContextValue {
  /**
   * Show the upgrade modal. Pass a feature key for auto-filled copy, or override
   * with explicit `title`/`description`/`targetTier`.
   *
   * Example:
   *   showUpgrade({ feature: 'SmartFill' });
   *   showUpgrade({ reason: 'limit', title: 'Booking form limit reached', contextNote: '...' });
   */
  showUpgrade: (config: UpgradePromptConfig) => void;
  /** Close the modal programmatically. */
  hideUpgrade: () => void;
}

const UpgradePromptContext = createContext<UpgradePromptContextValue | undefined>(undefined);

/**
 * Hook to surface plan upgrade modals at arbitrary click points.
 *
 *   const { showUpgrade } = useUpgradePrompt();
 *   const onClick = () => {
 *     if (!hasFeature) showUpgrade({ feature: 'SmartFill' });
 *     else doTheThing();
 *   };
 */
export function useUpgradePrompt(): UpgradePromptContextValue {
  const ctx = useContext(UpgradePromptContext);
  if (!ctx) {
    // Permissive fallback so calling code outside the provider doesn't crash —
    // useful for storybook / tests / public embed routes that never see plans.
    return {
      showUpgrade: () => {},
      hideUpgrade: () => {},
    };
  }
  return ctx;
}

interface UpgradePromptProviderProps {
  children: React.ReactNode;
}

/**
 * Single global UpgradeModal lives here. Any descendant can call
 * `useUpgradePrompt().showUpgrade(...)` from a click handler.
 *
 * Sits inside `PlanProvider` so the modal can pre-fill the current tier
 * without each call site passing it explicitly.
 */
export const UpgradePromptProvider: React.FC<UpgradePromptProviderProps> = ({ children }) => {
  const { tier, refresh } = usePlan();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [config, setConfig] = useState<UpgradePromptConfig | null>(null);

  const showUpgrade = useCallback((next: UpgradePromptConfig) => {
    setConfig(next);
    setIsOpen(true);
  }, []);

  const hideUpgrade = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
  }, []);

  const handlePromptComplete = useCallback(() => {
    setConfig(null);
  }, []);

  const value = useMemo<UpgradePromptContextValue>(() => ({ showUpgrade, hideUpgrade }), [showUpgrade, hideUpgrade]);

  const handleClose = () => {
    config?.onClose?.();
    setIsOpen(false);
  };

  const handleUpgraded = async () => {
    await refresh();
    config?.onUpgraded?.();
    setIsOpen(false);
    setConfig(null);
  };

  return (
    <UpgradePromptContext.Provider value={value}>
      {children}
      {config && (
        <UpgradeModal
          isOpen={isOpen}
          {...config}
          currentTier={config.currentTier ?? tier}
          onClose={handleClose}
          onUpgraded={handleUpgraded}
          onPromptComplete={handlePromptComplete}
        />
      )}
    </UpgradePromptContext.Provider>
  );
};
